import React, { FunctionComponent, MouseEvent, useCallback, useState } from 'react';

import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { isAuthorized } from 'auth/Auth';
import { useMessage, MessageActionType } from 'common/message-bar/MessageBar';
import { WarningMessageModal } from 'common/response-editor/warning-message/WarningMessageModal';
import { Overlay } from 'core/overlay/Overlay';
import { useReplies, RepliesActionType } from 'reviews2/feed/review-yeti/Replies.store';
import { ReplyProps, ResponseState } from 'reviews2/feed/review-yeti/reply/ReviewReply.types';
import { isPending, shouldManualPublish } from 'reviews2/feed/review-yeti/reply/ReviewReply.util';
import { useReview, ReviewActionType } from 'reviews2/feed/review-yeti/Review.store';
import { ReplyMode } from 'reviews2/feed/review-yeti/Review.types';
import { getResponse, getReview3, publish, reject } from 'reviews2/Reviews.api';

import 'reviews2/feed/review-yeti/reply/moderation/ReplyModeration.scss';

interface ReplyModerationProps extends ReplyProps {
    vobReviewResponseEnabled: boolean;
}

export const ReplyModeration: FunctionComponent<ReplyModerationProps> = ({
    context,
    reply,
    vobReviewResponseEnabled,
}) => {
    const { t } = useTranslation('reviews2');
    const { options, review } = context;
    const [{ disableModeration }, dispatch] = useReplies();
    const [, dispatchReview] = useReview();
    const [, messageDispatch] = useMessage();
    const OVERLAY_AREA = `reply-moderation-${reply.document_id}`;
    const [showWarning, setShowWarning] = useState(false);
    const canApprove = isAuthorized({ actions: [ActionId.ReviewResponses.Approve] });

    const moderationHandler = useCallback(
        async (approved: boolean) => {
            const fullReply = await trackPromise(getResponse(reply.r4e_tenant_id, reply.document_id), OVERLAY_AREA);

            if (isPending(fullReply)) {
                const publishRequest = {
                    id: fullReply.document_id,
                    parentID: fullReply.parent_id,
                    text: fullReply.raw_response_text,
                    manualPublish: shouldManualPublish(review),
                };
                let updatedReply;

                if (approved) {
                    updatedReply = await trackPromise(publish(reply.r4e_tenant_id, publishRequest), OVERLAY_AREA);
                } else {
                    updatedReply = await trackPromise(reject(reply.r4e_tenant_id, reply.document_id), OVERLAY_AREA);
                }

                const { review: updatedReview } = await trackPromise(
                    getReview3(review.r4e_tenant_id, review.document_id),
                    OVERLAY_AREA,
                );

                dispatchReview({
                    type: ReviewActionType.UpdateReview,
                    review: {
                        respondable_type: updatedReview.respondable_type,
                    },
                });

                dispatch({
                    type: RepliesActionType.SetEditReply,
                });

                dispatch({
                    type: RepliesActionType.SetReplyMode,
                    replyMode: ReplyMode.Add,
                });

                dispatch({
                    type: RepliesActionType.UpdateReply,
                    reply: updatedReply,
                });
            } else {
                dispatch({
                    type: RepliesActionType.UpdateReply,
                    reply: fullReply,
                });
                messageDispatch({
                    type: MessageActionType.ShowWarning,
                    text:
                        ResponseState.Rejected === fullReply.responseState
                            ? t('REPLY_ALREADY_REJECTED')
                            : t('REPLY_ALREADY_APPROVED'),
                });
            }
        },
        [dispatch, dispatchReview, messageDispatch, t, review, reply, OVERLAY_AREA],
    );

    const handleWarningDecision = async (approved: boolean) => {
        setShowWarning(false);

        if (approved) {
            await moderationHandler(true);
        }

        dispatch({
            type: RepliesActionType.ToggleModeration,
        });
    };

    const handler = async (approved: boolean, event: MouseEvent) => {
        event.stopPropagation();

        if (vobReviewResponseEnabled && approved && !reply.vob_threshold_met) {
            dispatch({
                type: RepliesActionType.ToggleModeration,
            });

            setShowWarning(true);
        } else {
            await moderationHandler(approved);
        }
    };

    const editHandler = useCallback(
        async (event: MouseEvent) => {
            event.stopPropagation();

            const fullReply = await trackPromise(getResponse(reply.r4e_tenant_id, reply.document_id), OVERLAY_AREA);

            if (isPending(fullReply)) {
                dispatch({
                    type: RepliesActionType.SetEditReply,
                    reply: fullReply,
                });
                dispatch({
                    type: RepliesActionType.SetReply,
                    reply: fullReply,
                });
                dispatch({
                    type: RepliesActionType.SetReplyMode,
                    replyMode: ReplyMode.Edit,
                });
            } else {
                dispatch({
                    type: RepliesActionType.UpdateReply,
                    reply: fullReply,
                });
                messageDispatch({
                    type: MessageActionType.ShowWarning,
                    text:
                        ResponseState.Rejected === fullReply.responseState
                            ? t('REPLY_ALREADY_REJECTED')
                            : t('REPLY_ALREADY_APPROVED'),
                });
            }
        },
        [dispatch, reply, messageDispatch, t, OVERLAY_AREA],
    );

    if (!isPending(reply) || options!.readOnly || options?.repliesOptions?.showModeration === false || !canApprove) {
        return null;
    }

    return (
        <div className="r4e-review-reply-moderation">
            {showWarning && <WarningMessageModal onDecision={handleWarningDecision} />}
            <Overlay area={OVERLAY_AREA} />
            <div className="r4e-review-reply-moderation-actions">
                <div className="left">
                    {isAuthorized({ actions: [ActionId.ReviewResponses.Edit] }) ? (
                        <Button
                            className="moderation-action-btn"
                            variant="secondary"
                            onClick={(event) => editHandler(event)}
                            disabled={disableModeration}
                        >
                            <span className="btn-text">{t('REPLY_EDIT')}</span>
                            <i className="icon-pencil btn-icon" />
                        </Button>
                    ) : null}
                </div>
                <div className="right">
                    {options?.renderReplyOptions?.(moderationHandler) || (
                        <>
                            <Button
                                className="moderation-action-btn"
                                variant="danger"
                                onClick={(event) => handler(false, event)}
                                disabled={disableModeration}
                            >
                                <span className="btn-text">{t('REPLY_REJECT')}</span>
                                <i className="icon-times btn-icon" />
                            </Button>
                            <Button
                                className="moderation-action-btn"
                                variant="primary"
                                onClick={(event) => handler(true, event)}
                                disabled={disableModeration}
                            >
                                <span className="btn-text">{t('REPLY_APPROVE')}</span>
                                <i className="icon-check btn-icon" />
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
